/* global Turbo */
import { Controller } from "@hotwired/stimulus"
import { get, post } from "@rails/request.js"

// Stimulus controller for the automation rule dry-run test panel.
// Record search/selection + test submission via Turbo Stream.
// Registered as: admin--automation--rule-test
export default class extends Controller {
  static targets = [
    "searchInput",
    "searchResults",
    "searchSpinner",
    "selectedRecord",
    "selectedRecordLabel",
    "recordId",
    "eventSelect",
    "testButton",
  ]
  static values = {
    searchUrl: String,
    testUrl: String,
    ruleId: String,
    formMode: { type: Boolean, default: false },
    formId: { type: String, default: "" },
    resultFrame: { type: String, default: "rule-test-result" },
    missingModelMessage: { type: String, default: "" },
  }

  // The rule form the panel reads live state from. When the panel lives inside
  // the form we use closest(); when it's rendered elsewhere (e.g. a drawer
  // outside the form) we resolve it by id.
  _ruleForm() {
    if (this.formIdValue) {
      const byId = document.getElementById(this.formIdValue)
      if (byId) return byId
    }
    return this.element.closest("form")
  }

  connect() {
    this._searchTimeout = null
    this._searchAbort = null
  }

  disconnect() {
    clearTimeout(this._searchTimeout)
    this._searchAbort?.abort()
  }

  search() {
    clearTimeout(this._searchTimeout)
    const query = this.searchInputTarget.value.trim()
    this._searchTimeout = setTimeout(() => this._performSearch(query), 300)
  }

  async _performSearch(query) {
    // Cancel any in-flight search so a slow earlier request can't resolve after
    // a faster later one and overwrite fresher results.
    this._searchAbort?.abort()
    const abort = new AbortController()
    this._searchAbort = abort

    try {
      const separator = this.searchUrlValue.includes("?") ? "&" : "?"
      let url = `${this.searchUrlValue}${separator}q=${encodeURIComponent(query)}`

      if (this.formModeValue) {
        const model = this._currentModel()
        if (!model) {
          this._renderMessage(this.missingModelMessageValue)
          return
        }
        url += `&model=${encodeURIComponent(model)}`
      }

      // The records query can be slow on large tables — show a spinner.
      this._setSearchLoading(true)
      const response = await get(url, {
        responseKind: "json",
        signal: abort.signal,
      })

      if (abort.signal.aborted || !response.ok) return

      const data = await response.json
      this._renderResults(data.records || [])
    } catch (_e) {
      // Silently fail on search errors (including AbortError from a superseded request)
    } finally {
      // Only clear the spinner if we're still the latest request — a superseded
      // request settling late must not turn off the spinner a newer one turned on.
      if (abort === this._searchAbort) this._setSearchLoading(false)
    }
  }

  _setSearchLoading(loading) {
    if (!this.hasSearchSpinnerTarget) return
    this.searchSpinnerTarget.classList.toggle("hidden", !loading)
  }

  _escapeHtml(str) {
    const div = document.createElement("div")
    div.textContent = str
    return div.innerHTML.replace(/"/g, "&quot;").replace(/'/g, "&#39;")
  }

  _renderMessage(text) {
    const container = this.searchResultsTarget
    const div = document.createElement("div")
    div.className = "px-3 py-2 text-sm text-muted-foreground"
    div.textContent = text || "—"
    container.replaceChildren(div)
    container.classList.remove("hidden")
  }

  // In form mode the target model is whatever the (possibly unsaved) form
  // currently has selected — read it live instead of a rendered-in value.
  _currentModel() {
    const form = this._ruleForm()
    const select = form?.querySelector("[name='automation_rule[target_model]']")
    return select?.value || ""
  }

  _renderResults(records) {
    const container = this.searchResultsTarget

    if (!records.length) {
      this._renderMessage("—")
      return
    }

    container.innerHTML = records
      .map((record) => {
        const label = this._escapeHtml(record.label || record.id)
        return `
      <button type="button"
              class="w-full px-3 py-2 text-left hover:bg-muted text-sm truncate"
              data-action="click->admin--automation--rule-test#selectRecord"
              data-record-id="${this._escapeHtml(record.id)}"
              data-record-label="${label}">
        ${label}
      </button>
    `
      })
      .join("")

    container.classList.remove("hidden")
  }

  // recordId lives on a target's data-value (NOT a form input) so it never
  // collides with the rule form or the sibling test panel.
  get _recordId() {
    return this.recordIdTarget.dataset.value || ""
  }

  set _recordId(value) {
    this.recordIdTarget.dataset.value = value
  }

  selectRecord(event) {
    const button = event.currentTarget

    this._recordId = button.dataset.recordId
    this.selectedRecordLabelTarget.textContent = button.dataset.recordLabel
    this.selectedRecordTarget.classList.remove("hidden")
    this.searchResultsTarget.classList.add("hidden")
    this.searchInputTarget.value = ""
    this._setTestButtonEnabled(true)
  }

  clearSelection() {
    this._recordId = ""
    this.selectedRecordTarget.classList.add("hidden")
    this._setTestButtonEnabled(false)
  }

  // Ui::ButtonComponent renders both disabled and aria-disabled — keep them in sync
  _setTestButtonEnabled(enabled) {
    this.testButtonTarget.disabled = !enabled
    this.testButtonTarget.setAttribute("aria-disabled", String(!enabled))
  }

  async runTest() {
    const recordId = this._recordId
    if (!recordId) return

    // Only toggle the disabled state — never touch textContent: the button
    // renders an <svg> icon + text, so overwriting textContent would strip the
    // icon permanently after the first run.
    this._setTestButtonEnabled(false)

    try {
      const body = this._buildTestBody(recordId)
      // Tell the controller which frame to render into — supports the drawer +
      // (legacy) inline panels having distinct turbo-frame ids on one page.
      body.append("result_frame", this.resultFrameValue)

      const response = await post(this.testUrlValue, {
        body,
        responseKind: "turbo-stream",
      })

      if (response.ok) {
        const html = await response.text
        Turbo.renderStreamMessage(html)
      }
    } catch (_e) {
      // Error handled by Turbo
    } finally {
      this._setTestButtonEnabled(true)
    }
  }

  _buildTestBody(recordId) {
    const body = new FormData()
    body.append("record_id", recordId)
    body.append("simulated_event", this._selectedEvent())

    if (this.formModeValue) {
      // Read the (possibly unsaved) rule fields straight off the form by name.
      // The builders keep condition_tree / actions_json hidden inputs in sync
      // in real time, so this captures the live state. Reading explicit fields
      // (instead of serializing the whole form) keeps the two test panels and
      // the rule form fully decoupled.
      const form = this._ruleForm()
      if (form) {
        this._appendFormField(
          body,
          form,
          "automation_rule[name]",
          "automation_rule[name]",
        )
        this._appendFormField(
          body,
          form,
          "automation_rule[target_model]",
          "automation_rule[target_model]",
        )
        this._appendFormField(
          body,
          form,
          "automation_rule[condition_tree]",
          "automation_rule[condition_tree]",
        )
        this._appendFormField(
          body,
          form,
          "automation_rule[actions_json]",
          "automation_rule[actions_json]",
        )
        this._appendTriggerEvent(body, form)
      }
    } else {
      body.append("rule_id", this.ruleIdValue)
    }

    return body
  }

  _appendFormField(body, form, selectorName, bodyKey) {
    const el = form.querySelector(`[name='${selectorName}']`)
    if (el) body.append(bodyKey, el.value)
  }

  // trigger_event is a radio group — grab the checked one
  _appendTriggerEvent(body, form) {
    const checked = form.querySelector(
      "[name='automation_rule[trigger_event]']:checked",
    )
    if (checked) body.append("automation_rule[trigger_event]", checked.value)
  }

  _selectedEvent() {
    if (!this.hasEventSelectTarget) return "update"
    const select = this.eventSelectTarget.matches("select")
      ? this.eventSelectTarget
      : this.eventSelectTarget.querySelector("select")
    return select?.value || "update"
  }
}
